import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import ActivityDto from '@/models/Activities/ActivityDto';
import ActivityCategoryDto from '@/models/Activities/ActivityCategoryDto';
import ActivityType from '@/models/Activities/ActivityType';
import ActivityStatus from '@/models/Activities/ActivityStatus';
import ActivityEntityType from '@/models/Activities/ActivityEntityType';

export interface IActivityTimelineState {
    loading: boolean;
    loadingMore: boolean;
    loadingDialog: boolean;
    activities: ActivityDto[];
    categories: ActivityCategoryDto[];
    totalCount: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
    // Filter state
    entityType: ActivityEntityType | null;
    entityId: string | null;
    activityTypes: ActivityType[];
    statuses: ActivityStatus[];
    categoryIds: string[];
    searchTerm: string;
    includeRelated: boolean;
}

// Helper function to build the API path
function getApiPath(entityType: ActivityEntityType | null, entityId: string | null): string {
    if (!entityType || !entityId) {
        return '/api/v1/agency/activities';
    }
    switch (entityType) {
        case ActivityEntityType.Contact:
            return `/api/v1/agency/contacts/${entityId}/activities`;
        case ActivityEntityType.Worker:
            return `/api/v1/agency/workers/${entityId}/activities`;
        case ActivityEntityType.Company:
            return `/api/v1/agency/companies/${entityId}/activities`;
        case ActivityEntityType.Location:
            return `/api/v1/agency/locations/${entityId}/activities`;
        case ActivityEntityType.Job:
            return `/api/v1/agency/jobs/${entityId}/activities`;
        case ActivityEntityType.Booking:
            return `/api/v1/agency/bookings/${entityId}/activities`;
        case ActivityEntityType.Opportunity:
            return `/api/v1/agency/opportunities/${entityId}/activities`;
        default:
            return '/api/v1/agency/activities';
    }
}

// Helper function to build query params
function buildQueryParams(page: number, pageSize: number, activityTypes: ActivityType[], statuses: ActivityStatus[], categoryIds: string[], includeRelated: boolean): string {
    const params: string[] = [];

    // Pagination - backend uses limit/offset
    params.push(`limit=${pageSize}`);
    params.push(`offset=${(page - 1) * pageSize}`);

    // Activity types filter
    if (activityTypes && activityTypes.length > 0) {
        activityTypes.forEach(type => {
            params.push(`activityTypes=${type}`);
        });
    }

    // Statuses filter
    if (statuses && statuses.length > 0) {
        statuses.forEach(s => {
            params.push(`statuses=${s}`);
        });
    }

    // Category IDs filter
    if (categoryIds && categoryIds.length > 0) {
        categoryIds.forEach(id => {
            params.push(`categoryIds=${id}`);
        });
    }

    // Include related (only sent when false, true is the backend default)
    if (!includeRelated) {
        params.push('includeRelated=false');
    }

    return params.join('&');
}

@Module({ dynamic: true, store, name: 'ActivityTimeline', namespaced: true })
class ActivityTimeline extends VuexModule implements IActivityTimelineState {
    public loading = false;
    public loadingMore = false;
    public loadingDialog = false;
    public activities: ActivityDto[] = [];
    public categories: ActivityCategoryDto[] = [];
    public totalCount = 0;
    public page = 1;
    public pageSize = 20;
    public hasMore = false;
    // Filter state
    public entityType: ActivityEntityType | null = null;
    public entityId: string | null = null;
    public activityTypes: ActivityType[] = [];
    public statuses: ActivityStatus[] = [];
    public categoryIds: string[] = [];
    public searchTerm = '';
    public includeRelated = true;

    @Action
    public async setContext(params: { entityType: ActivityEntityType; entityId: string }) {
        this.context.commit('setEntityContext', params);
    }

    @Action
    public async loadTimeline() {
        this.context.commit('setLoading', true);
        this.context.commit('resetPagination');

        // No types selected — return empty results without calling the API
        if (this.activityTypes.length === 0) {
            this.context.commit('setActivities', []);
            this.context.commit('setTotalCount', 0);
            this.context.commit('setHasMore', false);
            this.context.commit('setLoading', false);
            return;
        }

        const apiPath = getApiPath(this.entityType, this.entityId);
        const queryParams = buildQueryParams(1, this.pageSize, this.activityTypes, this.statuses, this.categoryIds, this.includeRelated);

        try {
            const response = await ajax.get(`${apiPath}?${queryParams}`);
            const data = response.data || {};
            const activities = data.activities || [];
            const total = data.total || 0;

            this.context.commit('setActivities', activities);
            this.context.commit('setTotalCount', total);
            this.context.commit('setHasMore', activities.length === this.pageSize);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.context.commit('setLoading', false);
        }
    }

    @Action
    public async loadMore() {
        if (!this.hasMore || this.loadingMore) return;

        this.context.commit('setLoadingMore', true);
        this.context.commit('incrementPage');

        const apiPath = getApiPath(this.entityType, this.entityId);
        const queryParams = buildQueryParams(this.page + 1, this.pageSize, this.activityTypes, this.statuses, this.categoryIds, this.includeRelated);

        try {
            const response = await ajax.get(`${apiPath}?${queryParams}`);
            const data = response.data || {};
            const activities = data.activities || [];

            this.context.commit('appendActivities', activities);
            this.context.commit('setHasMore', activities.length === this.pageSize);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.context.commit('setLoadingMore', false);
        }
    }

    @Action
    public async loadCategories() {
        try {
            const response = await ajax.get('/api/v1/agency/activity-categories');
            this.context.commit('setCategories', response.data || []);
        } catch (error) {
            AppModule.handleApiError(error);
        }
    }

    @Action
    public async updateActivityStatus(params: { activityId: string; status: number; updatedAt: Date | null }) {
        this.context.commit('setLoadingDialog', true);

        try {
            const response = await ajax.put(`/api/v1/agency/activities/${params.activityId}/status`, {
                status: params.status,
                updatedAt: params.updatedAt
            });
            this.context.commit('updateActivityInList', response.data);
        } catch (error) {
            AppModule.handleApiError(error);
            throw error;
        } finally {
            this.context.commit('setLoadingDialog', false);
        }
    }

    @Action
    public async deleteActivity(activityId: string) {
        this.context.commit('setLoadingDialog', true);

        try {
            await ajax.delete(`/api/v1/agency/activities/${activityId}`);
            this.context.commit('removeActivityFromList', activityId);
        } catch (error) {
            AppModule.handleApiError(error);
            throw error;
        } finally {
            this.context.commit('setLoadingDialog', false);
        }
    }

    @Action
    public async completeTask(activityId: string) {
        const activity = this.activities.find(a => a.id === activityId);
        if (!activity) return;

        await this.updateActivityStatus({
            activityId,
            status: ActivityStatus.Completed,
            updatedAt: activity.updatedAt
        });
    }

    @Action
    public async setFilters(filters: {
        activityTypes?: ActivityType[];
        statuses?: ActivityStatus[];
        categoryIds?: string[];
        searchTerm?: string;
    }) {
        this.context.commit('updateFilters', filters);
        await this.loadTimeline();
    }

    @Action
    public async setIncludeRelated(value: boolean) {
        this.context.commit('commitIncludeRelated', value);
        await this.loadTimeline();
    }

    @Action
    public async cleanState() {
        this.context.commit('resetState');
    }

    @Mutation
    private setLoading(isLoading: boolean) {
        this.loading = isLoading;
    }

    @Mutation
    private setLoadingMore(isLoading: boolean) {
        this.loadingMore = isLoading;
    }

    @Mutation
    private setLoadingDialog(isLoading: boolean) {
        this.loadingDialog = isLoading;
    }

    @Mutation
    private setEntityContext(params: { entityType: ActivityEntityType; entityId: string }) {
        this.entityType = params.entityType;
        this.entityId = params.entityId;
    }

    @Mutation
    private setActivities(activities: ActivityDto[]) {
        this.activities = activities;
    }

    @Mutation
    private appendActivities(activities: ActivityDto[]) {
        this.activities = [...this.activities, ...activities];
    }

    @Mutation
    private prependActivity(activity: ActivityDto) {
        this.activities = [activity, ...this.activities];
        this.totalCount++;
    }

    @Mutation
    private updateActivityInList(activity: ActivityDto) {
        const index = this.activities.findIndex(a => a.id === activity.id);
        if (index !== -1) {
            this.activities.splice(index, 1, activity);
        }
    }

    @Mutation
    private removeActivityFromList(activityId: string) {
        const index = this.activities.findIndex(a => a.id === activityId);
        if (index !== -1) {
            this.activities.splice(index, 1);
            this.totalCount--;
        }
    }

    @Mutation
    private setCategories(categories: ActivityCategoryDto[]) {
        this.categories = categories;
    }

    @Mutation
    private setTotalCount(count: number) {
        this.totalCount = count;
    }

    @Mutation
    private setHasMore(hasMore: boolean) {
        this.hasMore = hasMore;
    }

    @Mutation
    private resetPagination() {
        this.page = 1;
        this.activities = [];
    }

    @Mutation
    private incrementPage() {
        this.page++;
    }

    @Mutation
    private updateFilters(filters: {
        activityTypes?: ActivityType[];
        statuses?: ActivityStatus[];
        categoryIds?: string[];
        searchTerm?: string;
    }) {
        if (filters.activityTypes !== undefined) {
            this.activityTypes = filters.activityTypes;
        }
        if (filters.statuses !== undefined) {
            this.statuses = filters.statuses;
        }
        if (filters.categoryIds !== undefined) {
            this.categoryIds = filters.categoryIds;
        }
        if (filters.searchTerm !== undefined) {
            this.searchTerm = filters.searchTerm;
        }
    }

    @Mutation
    private commitIncludeRelated(value: boolean) {
        this.includeRelated = value;
    }

    @Mutation
    private resetState() {
        this.loading = false;
        this.loadingMore = false;
        this.loadingDialog = false;
        this.activities = [];
        this.categories = [];
        this.totalCount = 0;
        this.page = 1;
        this.hasMore = false;
        this.entityType = null;
        this.entityId = null;
        this.activityTypes = [];
        this.statuses = [];
        this.categoryIds = [];
        this.searchTerm = '';
        this.includeRelated = true;
    }
}

export const ActivityTimelineModule = getModule(ActivityTimeline);
